import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { fetchArticlesLite } from "../lib/api";
import { ArrowUpRight } from "lucide-react";
import { buildSrcSet, GUIDE_CARD_SIZES } from "../lib/image";
import { guidesAsArticles } from "../lib/guides";
import { pickHomepageGuides } from "../lib/articleSort";

export default function ContentHub() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    // Iter-275.AT (Phase 2 #4 of Production Readiness Audit) —
    // Homepage LCP was 9.3s on production because the legacy
    // `fetchArticles()` pulled `?limit=300` with full `body` text on
    // every page load (~1 MB payload) just to render 6 cards. The
    // lite endpoint strips body/quick_answer/rendered_body/body_excerpt
    // and we now fetch only the 36 we actually need to merge against
    // the static guides corpus + featured/pinned selector. Drops the
    // payload from ~1 MB → ~80 KB; expected LCP impact 9.3s → ≤4s.
    fetchArticlesLite({ limit: 36 })
      .then((data) => {
        // Iter-124 — Merge static guides with the API list so featured /
        // pinned status is evaluated across the full universe of guides
        // (the static ones don't live in MongoDB but still ship to
        // /guides/{slug} pages).
        const apiSlugs = new Set(data.map((a) => a.slug));
        const merged = [
          ...data,
          ...guidesAsArticles().filter((g) => !apiSlugs.has(g.slug)),
        ];
        setArticles(merged);
      })
      .catch(() => setArticles(guidesAsArticles()));
  }, []);

  // Iter-124 — Homepage editorial slot: pinned + featured first, then
  // fill remaining slots with the newest published guides. Always shows
  // up to 6 cards.
  const homepageGuides = useMemo(() => pickHomepageGuides(articles, 6), [articles]);

  return (
    <section className="bg-[#FAFAFA] py-24 lg:py-32">
      <div className="max-w-wide mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-4">
              CarSavr Guides
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-slate-950 max-w-3xl">
              Smarter ways to save on car ownership.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/guides"
              data-testid="view-all-articles"
              className="inline-flex items-center gap-2 font-bold text-slate-950 border-b border-slate-950 pb-1 hover:gap-3 transition-all"
            >
              All guides
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div data-testid="home-guides-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {homepageGuides.map((a) => (
            <Link
              key={a.slug}
              to={a.route_path || `/guides/${a.slug}`}
              data-testid={`article-card-${a.slug}`}
              className="group bg-white border border-slate-200 flex flex-col transition-all hover:-translate-y-1"
            >
              <div className="aspect-[16/10] overflow-hidden bg-slate-100 relative">
                <img
                  src={a.image_url}
                  srcSet={buildSrcSet(a.image_url) || undefined}
                  sizes={GUIDE_CARD_SIZES}
                  alt={a.image_alt || a.title}
                  width="800"
                  height="500"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Iter-124 — Promotion badge so users can see why a
                    guide is surfaced on the homepage at a glance. */}
                {(a.is_pinned || a.is_featured) && (
                  <span
                    data-testid={`home-guide-badge-${a.slug}`}
                    className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-950 bg-[#10B981] px-2 py-1"
                  >
                    {a.is_pinned ? "Pinned" : "Featured"}
                  </span>
                )}
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-950 bg-[#10B981] px-2 py-1">
                    {a.category}
                  </span>
                  <span className="text-xs text-slate-500">{a.read_time}</span>
                </div>
                <h3 className="font-display font-bold text-xl text-slate-950 leading-snug mb-3">
                  {a.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6 flex-1">{a.excerpt}</p>
                <div className="inline-flex items-center gap-2 font-bold text-sm text-slate-950">
                  Read guide
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
